<template>
  <div
      ref="container"
      class="zoom-container will-change-transform overflow-hidden"
      :style="{
      '--scale': data.scale,
      '--translate-x': `${data.translateX}px`,
      '--translate-y': `${data.translateY}px`,
    }"
      @mouseleave="endZoom"
      @mousemove="mouseMove($event)"
      @touchmove="touchMove($event)"
      @touchend="endZoom"
      @touchstart="touchStart($event)"
  >
    <div class="content" :class="{ 'block': !data.zooming,  'hidden': data.zooming }">
      <slot name="content"/>
    </div>
    <div
        class="zoomed-content"
        :class="{ 'block': data.zooming,  'hidden': !data.zooming }"
    >
      <slot name="zoomed-content"/>
    </div>
  </div>
</template>

<script setup>
import {reactive, ref} from "vue";

const props = defineProps({
  zoomTarget: Number
});
const diff = (a, b) => (a > b ? a - b : b - a);
const container = ref(null);
const data = reactive({
  scale: 1,
  translateX: 0,
  translateY: 0,
  zooming: false,
  startY: undefined,
  startZooming: undefined,
  isTouch: false
});
const touchStart = (event) => {
  data.isTouch = true;
  data.startY = event.touches[0].pageY;
  data.startZooming = setTimeout(() => (data.zooming = true), (1000 / 60) * 5);
  touchMove(event);
};
const endZoom = () => {
  if (data.startZooming) {
    clearTimeout(data.startZooming);
    data.startZooming = null;
  }
  data.zooming = false;
  data.scale = 1;
  data.translateY = data.translateX = 0;
};
const mouseMove = (event) => {
  // we don't want to run both touch and mouse
  if (!data.isTouch) {
    data.zooming = true;
    moveZoom(event.pageX, event.pageY);
  }
};
const touchMove = (event) => {
  const pageX = event.touches[0].pageX;
  const pageY = event.touches[0].pageY;

  const isUserAttemptingToScroll =
      diff(data.startY, pageY) > 10 && !data.zooming;
  const isUserAlreadyScrolling = !event.cancelable;
  if (isUserAttemptingToScroll || isUserAlreadyScrolling) {
    clearTimeout(data.startZooming);
    data.startZooming = null;
    return;
  }
  // Disabling the default of 'touchstart' would not allow scrolling at all
  if (event.type !== "touchstart") {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  moveZoom(pageX, pageY);
};
const moveZoom = (pageX, pageY) => {
  const containerInstance = container.value.getBoundingClientRect();
  const scale = (data.scale = props.zoomTarget / containerInstance.width);
  const XInContainer = pageX - containerInstance.left;
  const YInContainer = pageY - containerInstance.top;
  const centerX = containerInstance.width / 2;
  const centerY = containerInstance.height / 2;
  const ratioFromCenterX = (centerX - XInContainer) / centerX; // 1 is left, -1 is right
  const ratioFromCenterY = (centerY - YInContainer) / centerY; // 1 is top, -1 is bottom
  const differenceInWidth =
      containerInstance.width * scale - containerInstance.width;
  const differenceInHeight =
      containerInstance.height * scale - containerInstance.height;
  // distance is split between two sides
  const distanceToXZoomEdge = differenceInWidth / 2;
  const distanceToYZoomEdge = differenceInHeight / 2;
  // scaling applies to transforms as well
  data.translateX = (distanceToXZoomEdge / scale) * ratioFromCenterX;
  data.translateY = (distanceToYZoomEdge / scale) * ratioFromCenterY;
};
</script>
